'use client';

import type { InquireItem } from '@module/apis/client-side/customer/type';
import { itemsPerPage } from '@module/constants/pagenation';
import useGetLoginInfoQuery from '@module/helpers/auth/useGetLoginInfoQuery';
import { cn } from '@module/lib/utils';
import { useInquireListQuery } from '@module/queries/customer/customerQueryHooks';
import { convertISOToDottedShortDateString } from '@module/utils/date/convertDateFormat';
import BasePagenation from '@pc/components/pagenation/BasePagenation';
import NotFoundDisplay from '@pc/components/result/NotFoundDisplay';
import TableItemsTotalNumber from '@pc/components/table/TableItemsTotalNumber';

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@pc/components/ui/table';
import { keepPreviousData } from '@tanstack/react-query';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';

function InquireListContainer() {
  const router = useRouter();
  const { isLogin, isLoginInfoLoading } = useGetLoginInfoQuery();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const currentPageFirstOffset = (currentPage - 1) * itemsPerPage;
  const [suspendedAccountToken, setSuspendedAccountToken] = useState<string>(''); // 재재된 계정으로 들어왔을 경우 true

  const enabled = !!suspendedAccountToken || isLogin;

  const { data: inquireList, isLoading } = useInquireListQuery(
    {
      offset: currentPageFirstOffset,
      limit: itemsPerPage,
      ...(!!suspendedAccountToken && { token: suspendedAccountToken }),
    },
    {
      placeholderData: keepPreviousData,
      enabled,
      retry: false,
    },
  );

  const isListEmpty = !inquireList?.page.total;

  useEffect(() => {
    const { hash } = window.location;
    if (hash) {
      const token = hash.replace('#', '');
      setSuspendedAccountToken(token);
    }
  }, []);

  const handleItemClick = (item: InquireItem) => {
    let link = `/customer/inquire/${item.idx}`;
    if (window.location.hash) {
      link += window.location.hash;
    }
    router.push(link);
  };

  if (!enabled || isLoginInfoLoading || isLoading) return null;

  return (
    <div className='max-w-[1360px] px-10'>
      <TableItemsTotalNumber className='mb-4' count={inquireList?.page.total ?? 0} />
      <Table>
        <colgroup>
          <col width='120px' />
          <col width='15%' />
          <col width='auto' />
          <col width='15%' />
          <col width='15%' />
        </colgroup>
        <TableHeader>
          <TableRow className='h-[50px]'>
            <TableHead className='h-12'>{'번호'}</TableHead>
            <TableHead>{'분류'}</TableHead>
            <TableHead>{'제목'}</TableHead>
            <TableHead>{'문의일'}</TableHead>
            <TableHead>{'답변여부'}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {inquireList?.list.map((row, idx) => (
            <TableRow key={row.idx}>
              <TableCell>{inquireList.page.total - currentPageFirstOffset - idx}</TableCell>
              <TableCell>{row.categoryHuman}</TableCell>
              <TableCell className='max-w-0 text-left'>
                <button
                  type='button'
                  className={cn('block w-full truncate text-left text-colorFg19 leading-[24px]')}
                  onClick={() => handleItemClick(row)}
                >
                  {row.subject}
                </button>
              </TableCell>
              <TableCell>{convertISOToDottedShortDateString(row.insertDateTime)}</TableCell>
              <TableCell>
                {row.isAnswer ? <span className='text-[#ef312a]'>{'답변완료'}</span> : <span>{'답변대기'}</span>}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {!suspendedAccountToken && isListEmpty && <NotFoundDisplay text='1:1 문의 내역이 없습니다.' />}

      <BasePagenation currentPage={currentPage} setCurrentPage={setCurrentPage} pageData={inquireList?.page} />
      <div className='mt-[80px] flex justify-center '>
        <Link
          prefetch={false}
          href={suspendedAccountToken ? `/customer/inquire/write#${suspendedAccountToken}` : '/customer/inquire/write'}
          className='min-w-[120px] rounded-lg bg-primaryBg px-6 py-3 text-center text-[17px] text-colorFg12'
        >
          {'문의하기'}
        </Link>
      </div>
    </div>
  );
}

export default InquireListContainer;
